import _ from 'lodash'
import Backbone from 'backbone'
import Tooltip from 'scoreboard/components/tooltip'
import deviceType from 'scoreboard/components/device-type'
import appDisplay from 'scoreboard/components/app-display'
import LoadingDots from 'scoreboard/components/loading-dots'
import $ from 'jquery'
import i18n from 'scoreboard/components/i18n'

var TooltipAndDetails = Backbone.View.extend({

	/**
	 * Creates a tooltip that has a "More Details" type link that launches a
	 * dialog to show more details.  This component behaves differently
	 * depending on whether this is a touch device. Note the "More Details"
	 * part is totally optional. Without it, this is still a great component
	 * to use because it abstracts away the differences between showing a
	 * tooltip on touch vs click for touch vs non-touch devices.
	 *
	 * On non-touch devices, hovering an element that matches options.selector
	 * causes a tooltip to display.  Clicking on a matching element brings up
	 * a dialog showing more details.
	 *
	 * On touch devices, tapping on an element that matches options.selector
	 * launches a tooltip.  At the bottom of the tooltip there should be some
	 * sort of link the that can be tapped to bring up a dialog showing more
	 * details.
	 *
	 * Those rules were originally described by Scott here:
	 * https://github.com/SpiderStrategies/Scoreboard/issues/13114#issuecomment-327809565
	 *
	 * @constructs
	 *
	 * @param {Object} options
	 *
	 * @param {String|Element|JQuery} options.selector the selector to use to find the elements
	 * within this.el that should have tooltips attached to them
	 *
	 * @param {Function} options.tooltipContent a callback that returns the HTML
	 * to be displayed in the tooltip or a promise that resolves to HTML content.
	 * Should define an element with the class
	 * text-link which will be considered the "More Details" link.  If something
	 * falsy is returned, the tooltip won't display. This is not a part of options.tooltipOptions
	 * because this option needs to be passed the click event and is used before building the
	 * Tooltip to tell if it should be shown or not.
	 *
	 * @param {String|Element|JQuery} [options.attachTo] the element to attach the tooltip to.
	 * If not provided, the tooltip will be attached to the element that triggered the event.
	 * This is useful when you want the tooltip to be attached to a different element than
	 * the one being hovered/clicked, but generally this doesn't need to be set.
	 *
	 * @param {Object} options.tooltipOptions tooltip specific options which are
	 * to be passed directly to the Tooltip constructor.
	 *
	 * @param {Function} [options.details] a callback to be executed when details
	 * should be displayed.  If the callback returns a value, it will be
	 * displayed in a dialog. If the callback returns a Promise, it's resolved value
	 * will be displayed in a dialog.
	 *
	 * @param {Function} [options.preventTooltip] a function which is to be run (if provided)
	 * before the tooltip is displayed as an extra check making sure the tooltip should indeed be
	 * shown at the time of the mouseenter event. When the result of preventTooltip() is true,
	 * the tooltip will not show, else it will show.
	 */
	initialize: function(options = {}) {
		this.options = options
		this.activeElement = _.isString(options.selector) ?
			this.$(options.selector + ':not(.has-tooltip-and-details)') :
			// #25641 don't use this.$
			$(this.options.selector)

		// If the active element already has a tooltip assigned to it, prevent another
		// one from being added to that element.
		if (this.activeElement.hasClass('has-tooltip-and-details')) {
			return
		}

		this.activeElement
			.mouseenter(this.onMouseEnter.bind(this))
			.mouseleave(this.onMouseLeave.bind(this))
			.click(this.onClick.bind(this))
			.addClass('has-tooltip-and-details')
			.toggleClass('is-clickable', this.options.clickable)

		//#43288 see https://github.com/jquery/jquery/issues/2871#issuecomment-497963776
		//when in scroll pane make this a passive event listener for reasons
		//outlined here https://github.com/WICG/EventListenerOptions/blob/gh-pages/explainer.md
		let passive = this.activeElement.closest('.scroll-container').length > 0 ? true : false
		this.activeElement.each((idx,el) => {
			el.addEventListener('touchstart', this.onTouchStart.bind(this), { passive })
		})
	},

	onMouseEnter: function(e) {
		// If we're on a touchonly device we shouldn't be showing the tooltip on mouseenter
		if (deviceType.touchOnly) {
			return
		}

		// If a preventTooltip() function was provided and it's returning 'true'
		// don't show the tooltip
		if (_.result(this.options, 'preventTooltip')) {
			return
		}

		this.tooltip(e)
	},

	onMouseLeave: function(e) {
		// If the tooltip has scrollbar because its content is overflowing, we
		// won't remove the tooltip when the mouse leaves the hovered element so that
		// the user can scroll and see the content. Clicking outside the tooltip
		// will still remove it.
		let tooltipBody = this._tooltip ? this._tooltip.$el.find('.tooltip-body')[0] : false
		let isScrollable = tooltipBody ?
			tooltipBody.offsetHeight < tooltipBody.scrollHeight : ''

		if (deviceType.touchOnly || isScrollable) {
			this._tooltip?.$el.addClass('is-scrollable')
			return
		}
		this.removeTooltip(e)
	},

	onTouchStart: function(e) {
		this.tooltip(e)
	},

	onClick: function(e) {
		if (deviceType.touchOnly) {
			e.preventDefault()
			e.stopPropagation()
			return
		}
		this.options.details && this.details(e)
	},

	tooltip: function(e) {
		// Prevent duping the tooltip.
		this.removeTooltip()
		let content = this.options.tooltipContent(e)
		if (!content) {
			return
		}

		var displayContent = (content, extraTooltipClass) => {
			// If this._tooltip already exists, that means we were displaying the loading
			// dots. If that's the case, insert the content in to the existing tooltip and
			// nix the loading dots
			var tooltipTarget = e.target
			if (this._tooltip &&
				this._tooltip.$el.hasClass('has-loading-dots')) {
				this._tooltip.$el
					.removeClass('has-loading-dots')
				if (content instanceof Backbone.View) {
					this._tooltip.$el.html(content.el)
					content.render()
				} else {
					this._tooltip.$el.html(content)
				}
			// Else, just create this._tooltip like normal
			} else {
				this._tooltip = new Tooltip(_.extend({
					attachTo: this.options.attachTo || tooltipTarget,
					placement: this.options.placement || 'below',
					arrow: 'center',
					ignoreAttachToClick: true,
					closeOnAttachedToClick: false,
					content,
					extraClass: 'tooltip-and-details'
				}, this.options.tooltipOptions)).render()
			}

			// Add the extra class here instead of in the Tooltip() options because it might
			// get overridden if this.options.tooltipOptions has an extraClass defined
			extraTooltipClass && this._tooltip.$el.addClass(extraTooltipClass)

			// If we're on mobile, we need to hook up the 'details' action link
			if (deviceType.touchOnly && this.options.details) {
				// if there's no details link, add one
				if (_.isEmpty(this._tooltip.$('.view-details'))) {
					this._tooltip.$el.append(`
						<div class="view-details text-link">
							${i18n.get('common.details')}
						</div>`)
				}
				this._tooltip.$('.view-details').click(e => this.details(e, tooltipTarget))
			}

			this._tooltip.on('remove', () => {
				delete this._tooltip
			})
		}

		// If we're displaying content from a promise, first display the loading dots.
		if (_.isFunction(content.then)) {
			displayContent(new LoadingDots(), 'has-loading-dots')
			let myTooltip = this._tooltip
			content.then(markup => {
				// prevent multiple tooltips from being created if the tooltip
				// was removed before the promise resolved
				if (myTooltip === this._tooltip) {
					displayContent(markup)
				}
			// If the promise failed, display any given error or just remove the tooltip
			}).catch(err => {
				if (err) {
					displayContent(err)
				} else {
					this.removeTooltip()
				}
			})
		} else {
			displayContent(content)
		}
	},

	details: function(e, target = this.activeElement) {
		this.removeTooltip()
		$.when(this.options.details(e, target)).then(viewOrPromise => {
			appDisplay.dialog(viewOrPromise)
		})
	},

	removeTooltip: function(e) {
		_.invoke(this._tooltip, 'remove')
	},

	// remove() here is a done a little differently from in other views because
	// this.el here in this view is actually the view that created it - so
	// calling Backbone.View.prototype.remove() would have an undesired effect.
	remove: function() {
		this.removeTooltip()
		$(this.activeElement)
			.removeClass('has-tooltip-and-details is-clickable')
			.off('mouseenter')
			.off('mouseleave')
			.off('click')
		this.activeElement && this.activeElement.each((idx,el) => {
			el.removeEventListener('touchstart', this.onTouchStart.bind(this))
		})
		delete this.activeElement
		delete this.options
	}

})

export default TooltipAndDetails

TooltipAndDetails.__filename__ = 'components/tooltip-and-details.js'
